import React, { useState, useRef, memo, useEffect, useMemo } from 'react';
import cx from '@utils/cx.util';
import useVariation from '@hooks/useVariation';
import Slider from 'react-slick';
import { formatPercent, formatCurrency } from '../utils/formatters.util';
import ZoomImg from './ZoomImg';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import LimitedEditionLabel from '../components/tailwind/LimitedEditionLabel';
import NBCFLabel from '../components/tailwind/NBCFLabel';
import { PreviewRenderCanvas } from '@c2s-npm/plumreact';
import Ribbon from './tailwind/Ribbon';
import WebImage from './tailwind/WebImage';
import { BsArrowLeft, BsArrowRight, BsPlayCircleFill } from 'react-icons/bs';

const zoomLightboxInit = {
  current: null,
  next: null,
  prev: null,
};

type ProductImageSliderProps = {
  images?: any;
  stock?: string;
  displayName?: string;
  autoApplyDiscount?: any;
  productPromoBar?: any;
  compositeSlides?: any[];
  video?: any;
  prependSlides?: any[];
  skipPrependedSlides?: boolean;
  adaptiveHeight?: boolean;
  discountLabel?: string;
  showLimitedEdition?: boolean;
  isNBCF?: boolean;
  miyo?: any;
  isBundle?: boolean;
};

const ProductImageSlider = ({
  images,
  stock,
  displayName,
  autoApplyDiscount,
  productPromoBar,
  compositeSlides = [],
  video = null,
  prependSlides = [],
  skipPrependedSlides = false,
  adaptiveHeight,
  discountLabel,
  showLimitedEdition,
  isNBCF,
  miyo,
  isBundle,
}: ProductImageSliderProps) => {
  const [activeSlide, setActiveSlide] = useState(skipPrependedSlides ? prependSlides.length : 0);
  const slider = useRef(null);
  const sliderBullets = useRef(null);
  const isVariant = useVariation('Enhanced PDP');

  const [zoomLightboxData, setZoomLightboxData] = useState(zoomLightboxInit);
  const isMarketplaceImagery = useVariation('Marketplace Imagery');

  const allImages = useMemo(
    () => [
      ...(images?.lifestyle ?? []),
      ...((isMarketplaceImagery && images?.retailer && images?.retailer.length > 0
        ? images?.retailer
        : images?.views) ?? []),
    ],
    [images, isMarketplaceImagery]
  );

  useEffect(() => {
    if (skipPrependedSlides && activeSlide < prependSlides.length) {
      sliderGoTo(prependSlides.length, true)();
    }
  }, [skipPrependedSlides, prependSlides]);

  const zoomImgHandler = (src) => () => {
    const currentIndex = allImages.findIndex((view) => view.zoomed.url === src);
    const next = allImages.length > currentIndex + 1 ? allImages[currentIndex + 1].zoomed.url : null;
    const prev = currentIndex - 1 >= 0 ? allImages[currentIndex - 1].zoomed.url : null;

    setZoomLightboxData({
      current: src,
      next,
      prev,
    });
  };

  const sliderGoTo =
    (nextSlide, dontAnimate = false) =>
    () => {
      slider.current.slickGoTo(nextSlide, dontAnimate);
    };

  useEffect(() => {
    if (activeSlide < 0 && allImages?.length > 0) {
      const newActiveSlide = skipPrependedSlides ? prependSlides.length : 0;
      setActiveSlide(newActiveSlide);
      sliderGoTo(newActiveSlide)();
    }
  }, [allImages?.length]);

  const renderSlides = (slides = []) => {
    for (const slide of prependSlides) {
      slides.push(
        <div
          key={slides.length}
          className={cx(
            'tw:flex! tw:m-auto tw:items-center tw:justify-center tw:[&_img]:object-contain tw:[&_img]:!max-h-[300px] tw:md:[&_img]:!max-h-[400px] tw:lg:[&_img]:!max-h-[600px] tw:max-w-[600px] tw:max-h-[600px]',
            {
              'tw:overflow-hidden tw:before:contents-["Sold Out"] tw:before:text-white tw:before:bg-core-flame tw:before:text-sm tw:before:w-[170px] tw:before:right-auto tw:before:left:[-45px] tw:before:top-4 tw:sm:before:text-xl tw:sm:before:left-[-50px] tw:sm:before:w-[200px] tw:sm:before:top-6 tw:before:text-center tw:before:uppercase tw:before:absolute tw:before:font-bold tw:before:leading-[50px] tw:before:tracking-[1px] tw:before:shadow-[0_0_3px_rgba(0,0,0,0.3)] tw:before:z-[10] tw:before:rotate-[-45deg]':
                stock === 'soldOut',
            }
          )}>
          <WebImage
            webImage={slide.active}
            alt={`${displayName} Image`}
            className='zoom img-fluid'
            data-zoom-image={slide.zoomed.url}
          />
        </div>
      );
    }

    compositeSlides.forEach(({ slide }) =>
      slides.push(
        <div
          key={slides.length}
          className={cx(
            'tw:flex! tw:m-auto tw:items-center tw:justify-center tw:[&_img]:object-contain tw:max-w-[600px] tw:lg:h-[600px] tw:md:h-[400px] tw:h-[300px]',
            {
              'tw:overflow-hidden tw:before:contents-["Sold Out"] tw:before:text-white tw:before:bg-core-flame tw:before:text-sm tw:before:w-[170px] tw:before:right-auto tw:before:left:[-45px] tw:before:top-4 tw:sm:before:text-xl tw:sm:before:left-[-50px] tw:sm:before:w-[200px] tw:sm:before:top-6 tw:before:text-center tw:before:uppercase tw:before:absolute tw:before:font-bold tw:before:leading-[50px] tw:before:tracking-[1px] tw:before:shadow-[0_0_3px_rgba(0,0,0,0.3)] tw:before:z-[10] tw:before:rotate-[-45deg]':
                stock === 'soldOut',
            }
          )}>
          {slide}
        </div>
      )
    );

    if (video) {
      slides.push(
        <div className='tw:h-[300px] tw:lg:h-[600px]'>
          <video
            controls={false}
            title='Featured Video'
            autoPlay
            playsInline
            loop
            muted
            {...(video?.poster ? { poster: video.poster.url } : {})}
            className='tw:z-0 tw:h-full tw:w-full tw:rounded-3xl tw:object-contain tw:object-center'>
            <source src={video.original.url} type='video/mp4' />
          </video>
        </div>
      );
    }

    for (let i = 0; i < allImages.length; i++) {
      slides.push(
        <div
          key={slides.length}
          className={cx(
            'tw:flex! tw:m-auto tw:items-center tw:justify-center tw:[&_img]:object-contain tw:[&_img]:!max-h-[300px] tw:md:[&_img]:!max-h-[400px] tw:lg:[&_img]:!max-h-[600px] tw:max-w-[600px] tw:max-h-[600px]',
            {
              'tw:overflow-hidden tw:before:contents-["Sold Out"] tw:before:text-white tw:before:bg-core-flame tw:before:text-sm tw:before:w-[170px] tw:before:right-auto tw:before:left:[-45px] tw:before:top-4 tw:sm:before:text-xl tw:sm:before:left-[-50px] tw:sm:before:w-[200px] tw:sm:before:top-6 tw:before:text-center tw:before:uppercase tw:before:absolute tw:before:font-bold tw:before:leading-[50px] tw:before:tracking-[1px] tw:before:shadow-[0_0_3px_rgba(0,0,0,0.3)] tw:before:z-[10] tw:before:rotate-[-45deg]':
                stock === 'soldOut',
            }
          )}>
          <ZoomImg
            onClick={zoomImgHandler(allImages[i].zoomed.url)}
            src={allImages[i].zoomed.url}
            alt={allImages[i].zoomed.altText ?? `${displayName} Image`}
          />
        </div>
      );
    }

    return slides;
  };

  const renderBulletSlides = (slides = []) => {

    for (const slide of prependSlides) {
      slides.push(
        <WebImage
          webImage={slide.active}
          alt={`${displayName} Image`}
          className='tw:max-h-full tw:max-w-full'
          data-zoom-image={slide.zoomed.url}
        />
      );
    }

    for (const { slide } of compositeSlides) {
      slides.push(
        <div
          key={slides.length}
          className='tw:flex! tw:m-auto tw:items-center tw:justify-center tw:[&_img]:object-contain tw:!h-[57px] tw:lg:!h-[92px]'>
          {slide}
        </div>
      );
    }

    if (video) {
      const videoIndex = prependSlides.length + compositeSlides.length;

      slides.push(
        <div className='tw:p-[2px]'>
          <div
            className={cx(
              'tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-xl tw:border-2 tw:p-1 tw:lg:h-[100px] tw:lg:w-[100px]',
              {
                'tw:border-core-flame': activeSlide === videoIndex,
                'tw:border-neutral-200': activeSlide !== videoIndex,
                'tw:size-[65px]': !isVariant,
                'tw:size-[85px]': isVariant,
              }
            )}>
            <div className='tw:flex tw:items-center tw:justify-center tw:rounded-full'>
              <BsPlayCircleFill
                className={cx('tw:size-10', {
                  'tw:text-core-flame': activeSlide === videoIndex,
                  'tw:text-neutral-400': activeSlide !== videoIndex,
                })}
              />
            </div>
          </div>
        </div>
      );
    }

    for (let i = 0; i < allImages.length; i++) {
      slides.push(
        <WebImage
          webImage={allImages[i].zoomed}
          alt={`${displayName} Image`}
          className='tw:max-h-full tw:max-w-full'
        />
      );
    }

    const mappedSlides = slides.map((slide, index) => (
      <div key={index} className='tw:p-[2px]'>
        <div
          className={cx(
            'tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-xl tw:border-2 tw:p-1 tw:lg:h-[100px] tw:lg:w-[100px]',
            {
              'tw:border-core-flame': index === activeSlide,
              'tw:border-transparent': index !== activeSlide,
              'tw:size-[65px]': !isVariant,
              'tw:size-[85px]': isVariant,
            }
          )}
          onClick={() => {
            sliderGoTo(index)();
          }}>
          {slide}
        </div>
      </div>
    ));

    return mappedSlides;
  };

  const hasDesign = !!miyo?.asset;

  const numSlides = (hasDesign ? 1 : 0) + prependSlides.length + compositeSlides.length + allImages.length;

  const autoApplyDiscountFormatted =
    autoApplyDiscount &&
    (autoApplyDiscount.applicableDiscount.discountType === 'percent'
      ? formatPercent(autoApplyDiscount.applicableDiscount.discount)
      : formatCurrency(autoApplyDiscount.applicableDiscount.discount));

  const saleRibbonOverlay = discountLabel && !isBundle && (
    <>
      <div className='tw:absolute tw:right-2 tw:top-5 tw:z-10 tw:p-2'>
        <Ribbon>{discountLabel}</Ribbon>
      </div>
    </>
  );

  const autoApplyDiscountIconOverlay = !saleRibbonOverlay && autoApplyDiscount && (
    <div className='gift-overlay gift-overlay-dynamic'>
      <div className='content'>
        <div className='discount'>{autoApplyDiscountFormatted}</div>
        OFF
      </div>
    </div>
  );
  const limitedEditionOverlay =
    !saleRibbonOverlay &&
    showLimitedEdition &&
    (isNBCF ? (
      <NBCFLabel className='tw:absolute tw:right-2 tw:top-2 tw:z-10' />
    ) : (
      <LimitedEditionLabel className='tw:absolute tw:right-2 tw:top-2 tw:z-10' />
    ));

  return (
    <React.Fragment>
      <div
        className={cx(
          'tw:relative tw:[&_.slick-dots_li]:mx-1 tw:[&_.slick-dots_button]:h-2 tw:[&_.slick-dots_button]:w-2',
          {
            'tw:mr-5': !!saleRibbonOverlay,
          }
        )}>
        {autoApplyDiscountIconOverlay}
        {saleRibbonOverlay}
        {limitedEditionOverlay}

        <Slider
          slidesToShow={1}
          slidesToScroll={1}
          arrows={false}
          infinite={false}
          beforeChange={(current, next) => setActiveSlide(next)}
          dots={false}
          adaptiveHeight={adaptiveHeight}
          responsive={[
            {
              breakpoint: 768,
              settings: {
                adaptiveHeight,
              },
            },
            {
              breakpoint: 767,
              settings: {
                adaptiveHeight,
              },
            },
          ]}
          ref={slider}
          asNavFor={sliderBullets.current}
          className={cx('slider-container', {
            'tw:pb-7 tw:pt-16': isVariant,
          })}>
          {renderSlides(hasDesign ? [(
            <div className='tw:flex! tw:m-auto tw:items-center tw:justify-center tw:[&_img]:object-contain tw:[&_img]:!max-h-[300px] tw:md:[&_img]:!max-h-[400px] tw:lg:[&_img]:!max-h-[600px]'>
              <PreviewRenderCanvas
                config={miyo.preview[0]}
                designUrl={miyo.asset.fileUrl}
                graphicType={'engraved'}
                size={'large'}
                zoom={1}
              />
            </div>
          )] : [])}
        </Slider>
      </div>

      {productPromoBar}

      {numSlides > 1 && (
        <>
          <div className='tw:mx-auto tw:w-full tw:max-w-[500px] tw:lg:max-w-[650px]'>
            <Slider
              ref={sliderBullets}
              asNavFor={slider.current}
              slidesToShow={5}
              slidesToScroll={1}
              variableWidth={numSlides < 5}
              arrows={numSlides > 5}
              infinite={false}
              dots={false}
              prevArrow={
                <SlickButtonFix>
                  <BsArrowLeft
                    className='tw:cursor-pointer'
                    size={28}
                    onClick={() => {
                      sliderGoTo(activeSlide - 1 === -1 ? numSlides - 1 : activeSlide - 1)();
                    }}
                  />
                </SlickButtonFix>
              }
              nextArrow={
                <SlickButtonFix>
                  <BsArrowRight
                    className='tw:cursor-pointer'
                    onClick={() => {
                      sliderGoTo(activeSlide + 1 === numSlides ? 0 : activeSlide + 1)();
                    }}
                    size={28}
                  />
                </SlickButtonFix>
              }
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 5,
                    variableWidth: numSlides < 5,
                    arrows: false,
                  },
                },
                {
                  breakpoint: 420,
                  settings: {
                    slidesToShow: 4,
                    variableWidth: numSlides < 4,
                    arrows: false,
                  },
                },
                {
                  breakpoint: 340,
                  settings: {
                    slidesToShow: 3,
                    variableWidth: numSlides < 3,
                    arrows: false,
                  },
                },
              ]}
              className={cx('slider-container', {
                'tw:!flex tw:items-center tw:justify-center tw:gap-4': numSlides >= 5,
                'tw:!flex tw:lg:!block': numSlides === 4,
              })}>
              {renderBulletSlides(hasDesign ? [(
                <div className='tw:flex! tw:m-auto tw:items-center tw:justify-center tw:[&_img]:object-contain tw:[&_img]:!max-h-[300px] tw:md:[&_img]:!max-h-[400px] tw:lg:[&_img]:!max-h-[600px]'>
                  <PreviewRenderCanvas
                    config={miyo.preview[0]}
                    designUrl={miyo.asset.fileUrl}
                    graphicType={'engraved'}
                    size={'large'}
                    zoom={1}
                  />
                </div>
              )] : [])}
            </Slider>
          </div>
        </>
      )}

      <style>
        {`
          .yarl__toolbar {
            background-color: rgba(0, 0, 0, 0.8);
            border-radius: 10px;
            top: 10px;
            right: 10px;
          }

          .yarl__container {
            background-color: rgba(0, 0, 0, 0.8);
            backdrop-filter: blur(10px);
          }
        `}
      </style>

      <Lightbox
        open={zoomLightboxData.current}
        controller={{ closeOnBackdropClick: true, closeOnPullUp: true, closeOnPullDown: true }}
        close={() => setZoomLightboxData(zoomLightboxInit)}
        slides={allImages.map((img) => ({ src: img.zoomed.url }))}
        plugins={[Zoom]}
        zoom={{
          zoomInMultiplier: 1.75,
          maxZoomPixelRatio: 5,
          doubleClickDelay: 100,
          doubleTapDelay: 100,
          scrollToZoom: true,
        }}
      />
    </React.Fragment>
  );
};

export default memo(ProductImageSlider);

const SlickButtonFix = (props) => {
  const { currentSlide, slideCount, children, ...rest } = props;

  return <span {...rest}>{children}</span>;
};
